import { getApiBaseUrl } from "@/lib/api-config";
import { type Phase, parsePhase } from "@/lib/sessions/phase";

export type CreateSessionFileInput = {
  filename: string;
  sizeBytes: number;
  contentType: string;
};

export type CreateSessionFilePlan = {
  fileId: string;
  filename: string;
  sizeBytes: number;
  contentType: string;
  partSize: number;
  multipart: boolean;
};

export type CreateSessionResult = {
  sessionId: string;
  sessionToken: string;
  shareToken: string;
  phase: Phase;
  title: string;
  files: CreateSessionFilePlan[];
};

export type ShareFileStatus = {
  filename: string;
  sizeBytes: number;
  phase: Phase;
};

export type SharePageResult = {
  title: string;
  phase: Phase;
  fileCount: number;
  uploadedCount: number;
  files: ShareFileStatus[];
};

export type SessionFileStatus = {
  fileId: string;
  filename: string;
  sizeBytes: number;
  phase: Phase;
  error: string | null;
};

export type SessionStatusResult = {
  sessionId: string;
  phase: Phase;
  title: string;
  files: SessionFileStatus[];
};

export type DownloadQuality = "original" | "proxy";

export type SessionClip = {
  fileId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  url: string;
  previewUrl: string;
  proxyStatus: string;
  posterUrl: string | null;
  downloadUrl: string;
  downloadProxyUrl: string | null;
};

export type ResultClip = SessionClip & {
  confidence: number;
  lowConfidence: boolean;
  /**
   * Identity similarity of this clip to each surfer, keyed by surfer number.
   * Drives the "clips that could be you" ranking on the review screen. Empty
   * when the clip has no usable embedding.
   */
  surferScores: Record<number, number>;
};

export type SurferGroup = {
  surferNumber: number;
  label: string;
  clipCount: number;
  thumbnailUrl: string | null;
  clips: ResultClip[];
};

export type SessionResults = {
  phase: Phase;
  groups: SurferGroup[];
  unassigned: ResultClip[];
};

export type InitUploadResult = {
  uploadId: string | null;
  partSize: number;
  multipart: boolean;
  putUrl: string | null;
};

export type PartUrl = {
  partNumber: number;
  url: string;
};

type ApiErrorBody = {
  detail?: string | Array<{ msg?: string }>;
};

class UploadApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "UploadApiError";
    this.status = status;
  }
}

function parseApiErrorDetail(body: ApiErrorBody, fallback: string) {
  if (typeof body.detail === "string") return body.detail;
  if (Array.isArray(body.detail) && body.detail[0]?.msg)
    return body.detail[0].msg;
  return fallback;
}

async function apiFetchJson<T>(
  path: string,
  options: RequestInit & { sessionToken?: string } = {},
): Promise<T> {
  const { sessionToken, headers, ...rest } = options;
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      ...headers,
    },
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as ApiErrorBody;
      detail = parseApiErrorDetail(body, detail);
    } catch {
      // ignore parse errors
    }
    throw new UploadApiError(detail, response.status);
  }

  return (await response.json()) as T;
}

async function apiFetchVoid(
  path: string,
  options: RequestInit & { sessionToken?: string } = {},
): Promise<void> {
  const { sessionToken, headers, ...rest } = options;
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      ...headers,
    },
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as ApiErrorBody;
      detail = parseApiErrorDetail(body, detail);
    } catch {
      // ignore parse errors
    }
    throw new UploadApiError(detail, response.status);
  }
}

export async function createSession(
  clientSessionId: string,
  files: CreateSessionFileInput[],
): Promise<CreateSessionResult> {
  const result = await apiFetchJson<{
    session_id: string;
    session_token: string;
    share_token: string;
    phase: string;
    title: string;
    files: Array<{
      file_id: string;
      filename: string;
      size_bytes: number;
      content_type: string;
      part_size: number;
      multipart: boolean;
    }>;
  }>("/sessions", {
    method: "POST",
    body: JSON.stringify({
      client_session_id: clientSessionId,
      files: files.map((file) => ({
        filename: file.filename,
        size_bytes: file.sizeBytes,
        content_type: file.contentType,
      })),
    }),
  });

  return {
    sessionId: result.session_id,
    sessionToken: result.session_token,
    shareToken: result.share_token,
    phase: parsePhase(result.phase),
    title: result.title,
    files: result.files.map((file) => ({
      fileId: file.file_id,
      filename: file.filename,
      sizeBytes: file.size_bytes,
      contentType: file.content_type,
      partSize: file.part_size,
      multipart: file.multipart,
    })),
  };
}

type SessionStatusBody = {
  session_id: string;
  phase: string;
  title: string;
  files: Array<{
    file_id: string;
    filename: string;
    size_bytes: number;
    phase: string;
    error: string | null;
  }>;
};

function sessionStatusFromBody(body: SessionStatusBody): SessionStatusResult {
  return {
    sessionId: body.session_id,
    phase: parsePhase(body.phase),
    title: body.title,
    files: body.files.map((file) => ({
      fileId: file.file_id,
      filename: file.filename,
      sizeBytes: file.size_bytes,
      phase: parsePhase(file.phase),
      error: file.error,
    })),
  };
}

type ClipBody = {
  file_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  url: string;
  preview_url: string;
  proxy_status: string;
  poster_url: string | null;
  download_url: string;
  download_proxy_url: string | null;
};

type ResultClipBody = ClipBody & {
  confidence: number;
  low_confidence: boolean;
  surfer_scores: Record<string, number>;
};

function clipFromBody(clip: ClipBody): SessionClip {
  return {
    fileId: clip.file_id,
    filename: clip.filename,
    contentType: clip.content_type,
    sizeBytes: clip.size_bytes,
    url: clip.url,
    previewUrl: clip.preview_url,
    proxyStatus: clip.proxy_status,
    posterUrl: clip.poster_url,
    downloadUrl: clip.download_url,
    downloadProxyUrl: clip.download_proxy_url,
  };
}

function resultClipFromBody(clip: ResultClipBody): ResultClip {
  return {
    ...clipFromBody(clip),
    confidence: clip.confidence,
    lowConfidence: clip.low_confidence,
    surferScores: Object.fromEntries(
      Object.entries(clip.surfer_scores ?? {}).map(([surferNumber, score]) => [
        Number(surferNumber),
        score,
      ]),
    ),
  };
}

type SurferGroupBody = {
  surfer_number: number;
  label: string;
  clip_count: number;
  thumbnail_url: string | null;
  clips: ResultClipBody[];
};

type ResultsBody = {
  phase: string;
  groups: SurferGroupBody[];
  unassigned: ResultClipBody[];
};

function surferGroupFromBody(group: SurferGroupBody): SurferGroup {
  return {
    surferNumber: group.surfer_number,
    label: group.label,
    clipCount: group.clip_count,
    thumbnailUrl: group.thumbnail_url,
    clips: group.clips.map(resultClipFromBody),
  };
}

function resultsFromBody(body: ResultsBody): SessionResults {
  return {
    phase: parsePhase(body.phase),
    groups: body.groups.map(surferGroupFromBody),
    unassigned: body.unassigned.map(resultClipFromBody),
  };
}

export async function getSessionStatus(
  sessionId: string,
  sessionToken: string,
): Promise<SessionStatusResult> {
  const result = await apiFetchJson<SessionStatusBody>(`/sessions/${sessionId}`, {
    sessionToken,
  });

  return sessionStatusFromBody(result);
}

export async function updateSessionTitle(
  sessionId: string,
  sessionToken: string,
  title: string,
): Promise<SessionStatusResult> {
  const trimmed = title.trim();
  const result = await apiFetchJson<SessionStatusBody>(`/sessions/${sessionId}`, {
    method: "PATCH",
    sessionToken,
    body: JSON.stringify({ title: trimmed || null }),
  });

  return sessionStatusFromBody(result);
}

export async function deleteSession(
  sessionId: string,
  sessionToken: string,
): Promise<void> {
  await apiFetchVoid(`/sessions/${sessionId}`, {
    method: "DELETE",
    sessionToken,
  });
}

export async function getSessionClips(
  sessionId: string,
  sessionToken: string,
): Promise<SessionClip[]> {
  const result = await apiFetchJson<{
    session_id: string;
    clips: ClipBody[];
  }>(`/sessions/${sessionId}/files`, {
    sessionToken,
  });

  return result.clips.map(clipFromBody);
}

export async function initUpload(
  sessionId: string,
  fileId: string,
  sessionToken: string,
): Promise<InitUploadResult> {
  const result = await apiFetchJson<{
    upload_id: string | null;
    part_size: number;
    multipart: boolean;
    put_url: string | null;
  }>(`/sessions/${sessionId}/files/${fileId}/upload/init`, {
    method: "POST",
    sessionToken,
  });

  return {
    uploadId: result.upload_id,
    partSize: result.part_size,
    multipart: result.multipart,
    putUrl: result.put_url,
  };
}

export async function getPartUrls(
  sessionId: string,
  fileId: string,
  sessionToken: string,
  partNumbers: number[],
): Promise<PartUrl[]> {
  const result = await apiFetchJson<{
    parts: Array<{ part_number: number; url: string }>;
  }>(`/sessions/${sessionId}/files/${fileId}/upload/parts`, {
    method: "POST",
    sessionToken,
    body: JSON.stringify({ part_numbers: partNumbers }),
  });

  return result.parts.map((part) => ({
    partNumber: part.part_number,
    url: part.url,
  }));
}

export async function completeUpload(
  sessionId: string,
  fileId: string,
  sessionToken: string,
  payload: {
    uploadId?: string | null;
    parts: Array<{ partNumber: number; etag: string }>;
  },
): Promise<void> {
  await apiFetchVoid(`/sessions/${sessionId}/files/${fileId}/upload/complete`, {
    method: "POST",
    sessionToken,
    body: JSON.stringify({
      upload_id: payload.uploadId ?? null,
      parts: payload.parts.map((part) => ({
        part_number: part.partNumber,
        etag: part.etag,
      })),
    }),
  });
}

export async function deleteSessionClips(
  sessionId: string,
  sessionToken: string,
  fileIds: string[],
): Promise<void> {
  await apiFetchVoid(`/sessions/${sessionId}/files/delete`, {
    method: "POST",
    sessionToken,
    body: JSON.stringify({ file_ids: fileIds }),
  });
}

export async function getSessionResults(
  sessionId: string,
  sessionToken: string,
): Promise<SessionResults> {
  const result = await apiFetchJson<ResultsBody>(`/sessions/${sessionId}/results`, {
    sessionToken,
  });
  return resultsFromBody(result);
}

export async function reassignClip(
  sessionId: string,
  sessionToken: string,
  fileId: string,
  surferNumber: number | null,
): Promise<SessionResults> {
  const result = await apiFetchJson<ResultsBody>(
    `/sessions/${sessionId}/clips/${encodeURIComponent(fileId)}/assignment`,
    {
      method: "PATCH",
      sessionToken,
      body: JSON.stringify({ surfer_number: surferNumber }),
    },
  );
  return resultsFromBody(result);
}

export async function renameSurfer(
  sessionId: string,
  sessionToken: string,
  surferNumber: number,
  label: string,
): Promise<SurferGroup> {
  const trimmed = label.trim();
  // A blank name resets the surfer to its generated "Surfer N" default; the
  // server owns that fallback, so send null and mirror what it resolves.
  const result = await apiFetchJson<SurferGroupBody>(
    `/sessions/${sessionId}/groups/${surferNumber}`,
    {
      method: "PATCH",
      sessionToken,
      body: JSON.stringify({ label: trimmed || null }),
    },
  );
  return surferGroupFromBody(result);
}

export async function getSharePage(shareToken: string): Promise<SharePageResult> {
  const result = await apiFetchJson<{
    title: string;
    phase: string;
    file_count: number;
    uploaded_count: number;
    files: Array<{
      filename: string;
      size_bytes: number;
      phase: string;
    }>;
  }>(`/share/${encodeURIComponent(shareToken)}`);

  return {
    title: result.title,
    phase: parsePhase(result.phase),
    fileCount: result.file_count,
    uploadedCount: result.uploaded_count,
    files: result.files.map((file) => ({
      filename: file.filename,
      sizeBytes: file.size_bytes,
      phase: parsePhase(file.phase),
    })),
  };
}

export async function getShareResults(
  shareToken: string,
  viewerId?: string,
): Promise<SessionResults> {
  const result = await apiFetchJson<ResultsBody>(
    `/share/${encodeURIComponent(shareToken)}/results`,
    viewerId ? { headers: { "X-Viewer-Id": viewerId } } : {},
  );
  return resultsFromBody(result);
}

/**
 * A share viewer's personal reassignment of a clip to a surfer (or to unassigned
 * when surferNumber is null). Scoped to viewerId, it only changes this viewer's
 * composed results and never the canonical grouping. Returns the freshly
 * composed results for this viewer.
 */
export async function reassignClipForViewer(
  shareToken: string,
  viewerId: string,
  fileId: string,
  surferNumber: number | null,
): Promise<SessionResults> {
  const result = await apiFetchJson<ResultsBody>(
    `/share/${encodeURIComponent(shareToken)}/clips/${encodeURIComponent(fileId)}/assignment`,
    {
      method: "PATCH",
      headers: { "X-Viewer-Id": viewerId },
      body: JSON.stringify({ surfer_number: surferNumber }),
    },
  );
  return resultsFromBody(result);
}

export async function getShareClips(shareToken: string): Promise<SessionClip[]> {
  const result = await apiFetchJson<{
    clips: ClipBody[];
  }>(`/share/${encodeURIComponent(shareToken)}/clips`);

  return result.clips.map(clipFromBody);
}

/**
 * Request a batch download of the given clips and return an absolute zip URL the
 * browser can navigate to. The server resolves the clips to a short-lived ticket
 * so the streamed zip GET needs no auth header.
 */
export async function createSessionDownload(
  sessionId: string,
  sessionToken: string,
  fileIds: string[],
  quality: DownloadQuality,
): Promise<string> {
  const result = await apiFetchJson<{ url: string }>(
    `/sessions/${sessionId}/downloads`,
    {
      method: "POST",
      sessionToken,
      body: JSON.stringify({ file_ids: fileIds, quality }),
    },
  );
  return `${getApiBaseUrl()}${result.url}`;
}

export async function createShareDownload(
  shareToken: string,
  viewerId: string,
  fileIds: string[],
  quality: DownloadQuality,
): Promise<string> {
  const result = await apiFetchJson<{ url: string }>(
    `/share/${encodeURIComponent(shareToken)}/downloads`,
    {
      method: "POST",
      headers: { "X-Viewer-Id": viewerId },
      body: JSON.stringify({ file_ids: fileIds, quality }),
    },
  );
  return `${getApiBaseUrl()}${result.url}`;
}

export { UploadApiError };
